/**
 * Standard (Classic 50/50) style-specific section renderers.
 * Extracted from Classic5050StyleStandard.jsx for use in GridEngine.
 *
 * These match the Default grid layout's visual appearance so that
 * switching grid layout only changes WHERE sections go, not HOW they look.
 */
import React from 'react'
import { useNavigate } from 'react-router-dom'
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign.js';
import Info from 'lucide-react/dist/esm/icons/info.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import InlineTranslationEdit from '../../components/InlineTranslationEdit'
import RichImageCarousel from '../../components/RichImageCarousel'
import CountdownTimerStyles from '../../components/CountdownTimerStyles'
import HeroPartnerGlassSelect from '../../components/HeroPartnerGlassSelect'
import useHeroContent from '../../hooks/useHeroContent'
import { formatCurrencyValue } from '../../utils/formatCurrency'
import { getDrawLifecycleState, isDrawLifecycleBlocking } from '../../utils/drawLifecycle'

// Hero: "Operated by" text + partner name + game title + description
export function StandardHeroSection({
  mainDraw, partner, t, editMode, handleTranslationUpdate, gameType = 'classic5050', sections, language,
}) {
  const heroContent = useHeroContent({
    mainDraw,
    partner,
    gameType,
    sections,
    fallbackTitle: gameType === 'modified5050' ? 'MODIFIED 50/50 JACKPOT' : 'JACKPOT',
    fallbackSubtitle: "By participating in this fundraiser you're directly supporting our community programs and services.",
    fallbackCta: 'Buy Tickets',
  })
  if (!mainDraw) return null
  return (
    <div className="mb-8 text-center">
      <div className="mb-6">
        <p className="text-sm text-gray-600 dark:text-gray-400 font-medium tracking-wide uppercase">
          <InlineTranslationEdit
            translationKey={heroContent.eyebrowKey}
            value={heroContent.eyebrowLabel}
            fallback="Operated by"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
            className="text-sm font-medium tracking-wide uppercase"
          />
          {heroContent.showOperatorName && (
            <span> {heroContent.operatorName}</span>
          )}
        </p>
      </div>
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
        <span>{heroContent.organizationName}</span>{' '}
        <InlineTranslationEdit
          translationKey={heroContent.bannerKey}
          value={heroContent.bannerText}
          fallback={heroContent.bannerFallback}
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
          className="uppercase"
        />
      </h1>
      <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
        <InlineTranslationEdit
          translationKey={heroContent.subtitleKey}
          value={heroContent.subtitleText}
          fallback={heroContent.subtitleFallback}
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
          multiline
        />
      </p>
      <HeroPartnerGlassSelect className="mx-auto mt-6 w-full max-w-md" />
    </div>
  )
}

// Live Draw: 2-column with slides + jackpot/countdown/CTA
export function StandardLiveDrawSection({
  mainDraw, slides, salesClosed, t, editMode, handleTranslationUpdate,
  purchaseNavigate, timeLeft, shouldAnimate, countdownStyle, language, gameType = 'classic5050',
}) {
  const navigate = useNavigate()
  if (!mainDraw) return null
  const c = t
  const drawDateLabel = mainDraw.draw_date
    ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : ''

  const lifecycleState = getDrawLifecycleState({
    draw: mainDraw,
    countdownComplete: timeLeft?.isComplete,
    gameType,
  })
  const purchasesBlocked = !mainDraw || salesClosed || isDrawLifecycleBlocking(lifecycleState)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 md:p-8 mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div>
          {slides && slides.length > 0 ? (
            <RichImageCarousel slides={slides} />
          ) : (
            <div className="aspect-video rounded-xl bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 border border-blue-100 dark:border-gray-700 flex items-center justify-center">
              <div className="text-center space-y-2">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <InlineTranslationEdit
                    translationKey="standard.heroPlaceholder"
                    value={c?.('standard.heroPlaceholder', 'Community Powered Jackpot')}
                    fallback="Community Powered Jackpot"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </div>
                <div className="text-4xl font-black text-gray-900 dark:text-white">
                  {formatCurrencyValue(mainDraw.current_jackpot, { language })}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              <Calendar className="w-4 h-4" />
              <InlineTranslationEdit
                translationKey="HomeStandardNextDraw"
                value={c?.('HomeStandardNextDraw', 'Next Draw')}
                fallback="Next Draw"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
              {drawDateLabel || c?.('HomeStandardDrawDatePending', 'Date to be announced')}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              <DollarSign className="w-4 h-4" />
              <InlineTranslationEdit
                translationKey="HomeStandardNextJackpot"
                value={c?.('HomeStandardNextJackpot', 'Current Jackpot')}
                fallback="Current Jackpot"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </div>
            <div className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white">
              {formatCurrencyValue(mainDraw.current_jackpot, { language })}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <CountdownTimerStyles
              timeLeft={timeLeft}
              shouldAnimate={shouldAnimate}
              style={countdownStyle}
            />
          </div>

          <div className="flex flex-wrap gap-3">
            {purchasesBlocked ? (
              <button
                disabled
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-semibold rounded-lg cursor-not-allowed"
              >
                <Ticket className="w-4 h-4" />
                <span>{t?.('salesClosed') || 'Sales Closed'}</span>
              </button>
            ) : (
              <button
                onClick={() => purchaseNavigate?.('/purchase')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-charity-primary text-white font-semibold rounded-lg shadow hover:bg-charity-primary-dark transition-colors"
              >
                <Ticket className="w-4 h-4" />
                <InlineTranslationEdit
                  translationKey="HomeStandardCTAButton"
                  value={c?.('HomeStandardCTAButton', 'Buy Tickets')}
                  fallback="Buy Tickets"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </button>
            )}
            <button
              onClick={() => navigate('/winners')}
              className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-semibold rounded-lg hover:border-blue-400 transition-colors"
            >
              <Trophy className="w-4 h-4" />
              <InlineTranslationEdit
                translationKey="HomeStandardRecentWinners"
                value={c?.('HomeStandardRecentWinners', 'View Winners')}
                fallback="View Winners"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Last Draw Info: standalone card showing last draw jackpot + winner line
export function StandardLastDrawInfoSection({
  mainDraw, t, editMode, handleTranslationUpdate, language,
}) {
  if (!mainDraw) return null
  const c = t

  const [randomWinnerName] = React.useState(() => {
    const winnerNames = ['Brenda Godwin', 'Sarah Mitchell', 'James Thompson', 'Maria Garcia']
    return winnerNames[Math.floor(Math.random() * winnerNames.length)]
  })

  const formatWinnerText = () => {
    return `${randomWinnerName} ${c?.('HomeStandardWasWinner', 'was a winner')}`
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
          <InlineTranslationEdit
            translationKey="HomeLastDraw"
            value={c?.('HomeLastDraw', 'Last Draw')}
            fallback="Last Draw"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
            className="text-sm font-bold uppercase tracking-wide"
          />
        </h2>
      </div>
      <div className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-2">
        {formatCurrencyValue(mainDraw.current_jackpot, { language })}
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
        <Trophy className="w-4 h-4 text-yellow-500" />
        {formatWinnerText()}
      </p>
    </div>
  )
}

// How It Works: game description card + partner logo + image slider
export function StandardHowItWorksSection({
  mainDraw, partner, slides, t, editMode, handleTranslationUpdate,
}) {
  const navigate = useNavigate()
  if (!mainDraw) return null
  const c = t

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            <InlineTranslationEdit
              translationKey="HomeHowItWorks"
              value={c?.('HomeHowItWorks', 'How It Works')}
              fallback="How It Works"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
              className="text-sm font-bold uppercase tracking-wide"
            />
          </h2>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <p>
            <InlineTranslationEdit
              translationKey="HomeHowItWorksLearnMoreClassic"
              value={c?.('HomeHowItWorksLearnMoreClassic', 'This is a Classic 50/50 draw. Buy your tickets for a chance to win half the growing jackpot.')}
              fallback="This is a Classic 50/50 draw. Buy your tickets for a chance to win half the growing jackpot. One lucky winner takes home 50% of the pot, while the other half goes to support our charity."
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
              multiline
            />
          </p>
          <button
            onClick={() => navigate('/how-to-play')}
            className="text-blue-600 dark:text-blue-400 hover:underline mt-2 text-sm font-medium"
          >
            <InlineTranslationEdit
              translationKey="HomeHowItWorksLearnMore"
              value={c?.('HomeHowItWorksLearnMore', 'Learn More')}
              fallback="Learn More"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </button>
        </div>
      </div>

      {partner?.partner_logo_url && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 border border-gray-200 dark:border-gray-700 shadow-sm text-center">
          <div className="flex justify-center mb-6">
            <img
              src={partner.partner_logo_url}
              alt={partner.partner_name || 'Partner Logo'}
              className="max-w-full max-h-64 object-contain"
            />
          </div>
          {partner.partner_description && (
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed max-w-2xl mx-auto">
              {partner.partner_description}
            </p>
          )}
        </div>
      )}

      {slides && slides.length > 0 && (
        <RichImageCarousel slides={slides} />
      )}
    </div>
  )
}
